// src/pages/AdminTournamentManager.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { auth, db, functions } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  limit,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const COPPER = "#B87333";

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleString([], {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function msToMMSS(ms) {
  const total = Math.max(0, Math.floor(Number(ms || 0) / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function entrantName(entrant) {
  return (
    entrant?.displayName ||
    entrant?.name ||
    entrant?.email ||
    entrant?.uid ||
    entrant?.id ||
    "Player"
  );
}

function formatFinishPlace(value) {
  const n = Number(value || 0);
  if (!n) return "—";
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  if (n % 10 === 1) return `${n}st`;
  if (n % 10 === 2) return `${n}nd`;
  if (n % 10 === 3) return `${n}rd`;
  return `${n}th`;
}

function segmentLabel(item) {
  if (!item) return "—";
  if (item.type === "break") return `Break — ${item.label || "Break"}`;
  const ante = Number(item.ante || 0);
  return `Level ${item.level || "—"} • ${Number(item.sb || 0).toLocaleString()}/${Number(
    item.bb || 0
  ).toLocaleString()}${ante > 0 ? ` • ${ante.toLocaleString()} ante` : ""}`;
}

function Pill({ children, tone = "zinc" }) {
  const cls =
    tone === "green"
      ? "border-green-500/30 bg-green-500/10 text-green-300"
      : tone === "red"
      ? "border-red-500/30 bg-red-500/10 text-red-300"
      : tone === "amber"
      ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
      : tone === "blue"
      ? "border-blue-500/30 bg-blue-500/10 text-blue-300"
      : "border-zinc-700 bg-zinc-900 text-zinc-300";

  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${cls}`}>
      {children}
    </span>
  );
}

function Metric({ label, value, subtext }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
      <div className="text-[11px] font-black uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-black text-zinc-100">{value}</div>
      {subtext ? <div className="mt-1 text-xs text-zinc-500">{subtext}</div> : null}
    </div>
  );
}

function PositionChip({ role }) {
  const normalized = String(role || "").trim().toUpperCase();
  if (!normalized) return null;

  const config =
    normalized === "D"
      ? {
          label: "D",
          title: "Dealer Button",
          className:
            "border-white bg-white text-black shadow-[0_0_0_2px_rgba(255,255,255,0.12)]",
        }
      : normalized === "SB"
      ? {
          label: "SB",
          title: "Small Blind",
          className:
            "border-amber-300 bg-amber-300 text-black shadow-[0_0_0_2px_rgba(252,211,77,0.10)]",
        }
      : normalized === "BB"
      ? {
          label: "BB",
          title: "Big Blind",
          className:
            "border-blue-400 bg-blue-600 text-white shadow-[0_0_0_2px_rgba(96,165,250,0.10)]",
        }
      : null;

  if (!config) return null;

  return (
    <span
      title={config.title}
      aria-label={config.title}
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-black tracking-tight ${config.className}`}
    >
      {config.label}
    </span>
  );
}

export default function AdminTournamentManager() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [entrants, setEntrants] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [now, setNow] = useState(Date.now());
  const [busyAction, setBusyAction] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [preflight, setPreflight] = useState(null);

  const [memberDirectory, setMemberDirectory] = useState([]);
  const [lateMemberUid, setLateMemberUid] = useState("");
  const [lateDealerAppreciation, setLateDealerAppreciation] = useState(false);

  const [elimTargetId, setElimTargetId] = useState("");
  const [elimById, setElimById] = useState("");
  const [manualIndex, setManualIndex] = useState(0);
  const [manualMinutes, setManualMinutes] = useState("0");
  const [manualSeconds, setManualSeconds] = useState("00");
  const [manualResume, setManualResume] = useState(false);

  const [movePlayerId, setMovePlayerId] = useState("");
  const [moveTable, setMoveTable] = useState("1");
  const [moveSeat, setMoveSeat] = useState("1");

  const [winnerId, setWinnerId] = useState("");
  const [showChop, setShowChop] = useState(false);
  const [chopAmounts, setChopAmounts] = useState({});
  const [chopFinishPlaces, setChopFinishPlaces] = useState({});
  const [leftToPlayFor, setLeftToPlayFor] = useState("0");

  const autoAdvanceLock = useRef(false);

  const callControl = useCallback(
    async (action, data = {}) => {
      setBusyAction(action);
      setError("");
      setSuccess("");

      try {
        const callable = httpsCallable(functions, "adminTournamentControl");
        const result = await callable({
          tournamentId: id,
          action,
          ...data,
        });

        if (result.data?.message) setSuccess(result.data.message);
        return result.data;
      } catch (err) {
        console.error(`Tournament manager action ${action} failed:`, err);
        const message =
          err?.message ||
          err?.details ||
          `Could not complete tournament action: ${action}.`;
        setError(message);
        throw err;
      } finally {
        setBusyAction("");
      }
    },
    [id]
  );

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let unsubTournament = null;
    let unsubEntrants = null;
    let unsubEvents = null;

    const start = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        const admin = userSnap.exists() && userSnap.data()?.role === "admin";
        setIsAdmin(admin);
        setCheckingAdmin(false);

        if (!admin) {
          navigate("/dashboard");
          return;
        }

        try {
          const membersSnap = await getDocs(collection(db, "users"));
          setMemberDirectory(
            membersSnap.docs
              .map((d) => ({ uid: d.id, ...d.data() }))
              .filter((member) => {
                const status = String(member?.status || "active").toLowerCase();
                return status !== "disabled" && status !== "suspended";
              })
              .sort((a, b) =>
                String(a?.name || a?.displayName || a?.email || "").localeCompare(
                  String(b?.name || b?.displayName || b?.email || "")
                )
              )
          );
        } catch (memberError) {
          console.error("Tournament Manager member directory failed:", memberError);
        }

        unsubTournament = onSnapshot(
          doc(db, "tournaments", id),
          (snap) => {
            setTournament(snap.exists() ? { id: snap.id, ...snap.data() } : null);
            setLoading(false);
          },
          (err) => {
            console.error("Tournament manager tournament snapshot failed:", err);
            setError("Could not read tournament data.");
            setLoading(false);
          }
        );

        unsubEntrants = onSnapshot(
          query(collection(db, "tournaments", id, "entrants"), orderBy("createdAt", "asc")),
          (snap) => setEntrants(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
          (err) => {
            console.error("Tournament manager entrants snapshot failed:", err);
            setError("Could not read entrant data.");
          }
        );

        unsubEvents = onSnapshot(
          query(
            collection(db, "tournaments", id, "events"),
            orderBy("createdAt", "desc"),
            limit(30)
          ),
          (snap) => setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
          (err) => console.error("Tournament manager event snapshot failed:", err)
        );
      } catch (err) {
        console.error("Tournament manager init failed:", err);
        setCheckingAdmin(false);
        setLoading(false);
        setError("Could not initialize Tournament Manager.");
      }
    };

    start();

    return () => {
      if (unsubTournament) unsubTournament();
      if (unsubEntrants) unsubEntrants();
      if (unsubEvents) unsubEvents();
    };
  }, [id, navigate]);

  const blinds = useMemo(
    () =>
      Array.isArray(tournament?.liveBlinds) && tournament.liveBlinds.length
        ? tournament.liveBlinds
        : Array.isArray(tournament?.blinds)
        ? tournament.blinds
        : [],
    [tournament]
  );

  const clock = useMemo(
    () =>
      tournament?.clock || {
        status: "stopped",
        index: 0,
        endsAt: null,
        remainingMs: null,
      },
    [tournament?.clock]
  );

  const isCompleted = tournament?.status === "completed";

  const isCompletedChopParticipant = useCallback(
    (entrant) =>
      isCompleted &&
      entrant?.chopParticipant === true &&
      Number(entrant?.finishPlace || 0) > 0,
    [isCompleted]
  );

  const activeEntrants = useMemo(
    () =>
      entrants.filter(
        (entrant) =>
          !entrant?.eliminated &&
          entrant?.tournamentFinished !== true &&
          !isCompletedChopParticipant(entrant)
      ),
    [entrants, isCompletedChopParticipant]
  );

  const positionRoleByEntrantId = useMemo(() => {
    const roleMap = new Map();
    const tables = new Map();

    activeEntrants.forEach((entrant) => {
      const explicitRole = String(
        entrant?.seatRole || entrant?.seatLabel || entrant?.position || ""
      )
        .trim()
        .toUpperCase();

      if (["D", "DEALER", "BTN", "BUTTON"].includes(explicitRole)) {
        roleMap.set(entrant.id, "D");
        return;
      }
      if (["SB", "SMALL BLIND", "SMALL_BLIND"].includes(explicitRole)) {
        roleMap.set(entrant.id, "SB");
        return;
      }
      if (["BB", "BIG BLIND", "BIG_BLIND"].includes(explicitRole)) {
        roleMap.set(entrant.id, "BB");
        return;
      }

      const tableNumber = Number(entrant?.tableNumber || 0);
      const seatNumber = Number(entrant?.seatNumber || 0);
      if (!tableNumber || !seatNumber) return;

      if (!tables.has(tableNumber)) tables.set(tableNumber, []);
      tables.get(tableNumber).push(entrant);
    });

    tables.forEach((players) => {
      const sorted = [...players].sort(
        (a, b) => Number(a?.seatNumber || 999) - Number(b?.seatNumber || 999)
      );

      if (sorted.length === 1) {
        roleMap.set(sorted[0].id, "D");
        return;
      }

      if (sorted.length === 2) {
        // Heads-up: dealer is also the small blind.
        // We show the dealer button on the first occupied seat and BB on the other.
        if (!roleMap.has(sorted[0].id)) roleMap.set(sorted[0].id, "D");
        if (!roleMap.has(sorted[1].id)) roleMap.set(sorted[1].id, "BB");
        return;
      }

      if (!roleMap.has(sorted[0].id)) roleMap.set(sorted[0].id, "D");
      if (!roleMap.has(sorted[1].id)) roleMap.set(sorted[1].id, "SB");
      if (!roleMap.has(sorted[2].id)) roleMap.set(sorted[2].id, "BB");
    });

    return roleMap;
  }, [activeEntrants]);

  const chopFinishedEntrants = useMemo(
    () =>
      entrants
        .filter((entrant) => isCompletedChopParticipant(entrant))
        .sort(
          (a, b) =>
            Number(a?.finishPlace || 9999) - Number(b?.finishPlace || 9999)
        ),
    [entrants, isCompletedChopParticipant]
  );

  const eliminatedEntrants = useMemo(
    () =>
      entrants
        .filter(
          (entrant) =>
            entrant?.eliminated && !isCompletedChopParticipant(entrant)
        )
        .sort(
          (a, b) =>
            Number(a?.finishPlace || 9999) - Number(b?.finishPlace || 9999)
        ),
    [entrants, isCompletedChopParticipant]
  );

  const bountyEnabled =
    tournament?.bountyEnabled === true ||
    String(tournament?.tournamentType || "").toLowerCase() === "bounty";
  const bountyAmount = bountyEnabled ? Math.max(0, Number(tournament?.bountyAmount || 0)) : 0;
  const prizePoolContribution = bountyEnabled
    ? Math.max(
        0,
        Number(
          tournament?.prizePoolContribution ??
            Math.max(0, Number(tournament?.buyIn || 0) - bountyAmount)
        )
      )
    : Math.max(0, Number(tournament?.buyIn || 0));

  const regularPrizePool = useMemo(() => {
    const paidEntries =
      Number(
        tournament?.paidEntryCount ||
          tournament?.entriesPaidCount ||
          tournament?.entrantsCount ||
          entrants.length
      ) || entrants.length;
    const totalRebuys = entrants.reduce(
      (sum, e) => sum + Math.max(0, Number(e?.rebuys || 0)),
      0
    );
    const totalAddOns = entrants.reduce((sum, e) => sum + (e?.addOnTaken ? 1 : 0), 0);
    const dealerPrizePool = entrants.reduce(
      (sum, e) =>
        sum + Math.max(0, Number(e?.dealerAppreciationPrizePoolAmount || 0)),
      0
    );

    return Math.max(
      0,
      paidEntries * prizePoolContribution +
        totalRebuys * prizePoolContribution +
        totalAddOns * Math.max(0, Number(tournament?.addOnCost || 0)) +
        dealerPrizePool
    );
  }, [tournament, entrants, prizePoolContribution]);

  const timeRemainingMs = useMemo(() => {
    if (clock?.status === "running" && clock?.endsAt) {
      const ends = toDate(clock.endsAt);
      return ends ? Math.max(0, ends.getTime() - now) : 0;
    }

    if (clock?.status === "paused") {
      return Math.max(
        0,
        Number(clock?.remainingMs ?? clock?.checkpointRemainingMs ?? 0)
      );
    }

    return 0;
  }, [clock, now]);

  const activeSegment = blinds[Number(clock?.index || 0)] || null;

  const lateRegistrationEnabled = tournament?.lateRegistrationEnabled === true;
  const lateRegistrationOpen =
    tournament?.status === "running" &&
    lateRegistrationEnabled &&
    tournament?.lateRegistrationOpen === true;
  const lateRegistrationCutoffLevel = Number(
    tournament?.lateRegistrationCutoffLevel || 0
  );

  const availableLateMembers = useMemo(() => {
    const entrantIds = new Set(
      entrants.map((entrant) => String(entrant.uid || entrant.id))
    );
    return memberDirectory.filter(
      (member) => !entrantIds.has(String(member.uid))
    );
  }, [memberDirectory, entrants]);

  useEffect(() => {
    const idx = Math.max(
      0,
      Math.min(Number(clock?.index || 0), Math.max(blinds.length - 1, 0))
    );
    setManualIndex(idx);

    let ms = Math.max(
      0,
      Number(
        clock?.remainingMs ??
          clock?.checkpointRemainingMs ??
          Number(blinds[idx]?.minutes || 0) * 60 * 1000
      )
    );

    if (clock?.status === "running" && clock?.endsAt) {
      const ends = toDate(clock.endsAt);
      if (ends) {
        ms = Math.max(0, ends.getTime() - Date.now());
      }
    }

    setManualMinutes(String(Math.floor(ms / 60000)));
    setManualSeconds(String(Math.floor((ms % 60000) / 1000)).padStart(2, "0"));
  }, [
    clock?.index,
    clock?.status,
    clock?.endsAt,
    clock?.remainingMs,
    clock?.checkpointRemainingMs,
    blinds,
  ]);

  useEffect(() => {
    if (!isAdmin || !tournament) return;
    if (clock?.status !== "running") {
      autoAdvanceLock.current = false;
      return;
    }
    if (timeRemainingMs > 0) {
      autoAdvanceLock.current = false;
      return;
    }
    if (autoAdvanceLock.current || busyAction) return;

    autoAdvanceLock.current = true;
    callControl("advance")
      .catch(() => {})
      .finally(() => {
        window.setTimeout(() => {
          autoAdvanceLock.current = false;
        }, 1500);
      });
  }, [isAdmin, tournament, clock?.status, timeRemainingMs, busyAction, callControl]);

  const runPreflight = async () => {
    const result = await callControl("preflight");
    setPreflight(result?.preflight || null);
  };

  const startClock = async () => {
    const registrationMessage = lateRegistrationEnabled
      ? `Late registration will remain open through Level ${
          lateRegistrationCutoffLevel || "the configured cutoff"
        }.`
      : "Registration will close when the tournament starts.";

    if (
      !window.confirm(
        `Start this tournament?\n\n${registrationMessage}`
      )
    ) {
      return;
    }

    await callControl("start");
  };

  const pauseClock = async () => {
    await callControl("pause");
  };

  const resumeClock = async () => {
    await callControl("resume");
  };

  const applyManualClock = async () => {
    const minutes = Math.max(0, Number(manualMinutes || 0));
    const seconds = Math.max(0, Math.min(59, Number(manualSeconds || 0)));
    const remainingMs = Math.round((minutes * 60 + seconds) * 1000);

    await callControl("set_clock", {
      index: Number(manualIndex || 0),
      remainingMs,
      resume: !!manualResume,
    });
  };

  const eliminate = async () => {
    if (!elimTargetId || !elimById) return;

    const target = entrants.find((entrant) => entrant.id === elimTargetId);
    const eliminator = entrants.find((entrant) => entrant.id === elimById);

    if (
      !window.confirm(
        `Record ${entrantName(eliminator)} eliminating ${entrantName(target)}?`
      )
    ) {
      return;
    }

    await callControl("eliminate", {
      entrantId: elimTargetId,
      eliminatorId: elimById,
    });
    setElimTargetId("");
    setElimById("");
  };

  const rebuy = async (entrant) => {
    if (!window.confirm(`Rebuy ${entrantName(entrant)} and return them to active play?`)) {
      return;
    }
    await callControl("rebuy", { entrantId: entrant.id });
  };

  const addOn = async (entrant) => {
    if (
      !window.confirm(
        `Apply the configured add-on to ${entrantName(entrant)}?`
      )
    ) {
      return;
    }
    await callControl("add_on", { entrantId: entrant.id });
  };

  const assignSeats = async () => {
    if (!window.confirm("Randomly balance and assign seats for all active players?")) return;
    await callControl("assign_seats");
  };

  const combineTables = async () => {
    if (
      !window.confirm(
        "Generate a new balanced seating draw and pause the clock for a table combine?"
      )
    ) {
      return;
    }
    await callControl("combine_tables");
  };

  const movePlayer = async () => {
    if (!movePlayerId) return;
    await callControl("move_player", {
      entrantId: movePlayerId,
      tableNumber: Number(moveTable || 1),
      seatNumber: Number(moveSeat || 1),
    });
  };

  const declareWinner = async () => {
    if (!winnerId) return;
    const winner = activeEntrants.find((e) => e.id === winnerId);
    if (!window.confirm(`Declare ${entrantName(winner)} the winner?`)) return;
    await callControl("declare_winner", { winnerId });
  };

  const openChop = () => {
    const nextAmounts = {};
    const nextPlaces = {};
    activeEntrants.forEach((entrant, index) => {
      nextAmounts[entrant.id] = "";
      nextPlaces[entrant.id] = String(index + 1);
    });
    setChopAmounts(nextAmounts);
    setChopFinishPlaces(nextPlaces);
    setLeftToPlayFor("0");
    setShowChop(true);
  };

  const addLateEntrant = async () => {
    if (!lateMemberUid) return;

    const member = memberDirectory.find(
      (candidate) => candidate.uid === lateMemberUid
    );

    if (
      !window.confirm(
        `Add ${
          member?.name || member?.displayName || member?.email || "this member"
        } as a late entrant?`
      )
    ) {
      return;
    }

    await callControl("add_late_entrant", {
      memberUid: lateMemberUid,
      dealerAppreciationOptIn: !!lateDealerAppreciation,
    });

    setLateMemberUid("");
    setLateDealerAppreciation(false);
  };

  const closeLateRegistration = async () => {
    if (
      !window.confirm(
        "Close late registration now? This also disables SMS registration for this tournament."
      )
    ) {
      return;
    }

    await callControl("close_late_registration");
  };

  const submitCustomChop = async () => {
    const left = Number(leftToPlayFor || 0);
    const isFullChop = left <= 0;
    const payouts = activeEntrants.map((entrant) => ({
      entrantId: entrant.id,
      amount: Number(chopAmounts[entrant.id] || 0),
      ...(isFullChop
        ? { finishPlace: Number(chopFinishPlaces[entrant.id] || 0) }
        : {}),
    }));
    const allocated = payouts.reduce((sum, row) => sum + row.amount, 0);

    if (Math.abs(allocated + left - regularPrizePool) > 0.01) {
      return alert(
        `Payouts + amount left to play for must equal ${formatMoney(regularPrizePool)}.`
      );
    }

    if (isFullChop) {
      const places = payouts.map((row) => row.finishPlace);
      const expectedPlaces = activeEntrants.map((_, index) => index + 1);
      const validPlaces =
        places.every((place) => Number.isInteger(place) && place >= 1 && place <= activeEntrants.length) &&
        new Set(places).size === activeEntrants.length &&
        expectedPlaces.every((place) => places.includes(place));

      if (!validPlaces) {
        return alert(
          `For a full chop, assign each remaining player a unique finish place from 1 through ${activeEntrants.length}.`
        );
      }
    }

    if (
      !window.confirm(
        left > 0
          ? `Record this custom chop and continue playing for ${formatMoney(left)}?`
          : "Record this custom chop and end the tournament?"
      )
    ) {
      return;
    }

    await callControl("custom_chop", {
      payouts,
      leftToPlayFor: left,
    });
    setShowChop(false);
  };

  if (checkingAdmin || loading) {
    return (
      <Layout>
        <div className="text-zinc-400">Loading Tournament Manager...</div>
      </Layout>
    );
  }

  if (!isAdmin) return null;

  if (!tournament) {
    return (
      <Layout>
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-red-200">
          Tournament not found.
        </div>
      </Layout>
    );
  }

  const canStart =
    !busyAction &&
    tournament.status !== "completed" &&
    tournament.status !== "cancelled" &&
    clock?.status !== "running" &&
    clock?.status !== "paused";

  const canPause = !busyAction && clock?.status === "running";
  const canResume =
    !busyAction &&
    clock?.status === "paused" &&
    !tournament?.addOnPause?.active &&
    !tournament?.tableCombine?.active;

  return (
    <Layout>
      <div className="mx-auto max-w-[1600px] space-y-6">
        <section className="relative overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-900 p-6 sm:p-8">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at top right, rgba(184,115,51,0.20), transparent 38%)",
            }}
          />
          <div className="relative flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.25em] text-[#B87333]">
                Admin • Tournament Control Center
              </div>
              <h1 className="mt-2 text-3xl font-black text-zinc-100 sm:text-4xl">
                {tournament.name || "Tournament"}
              </h1>
              <p className="mt-2 text-sm text-zinc-400">
                {formatDateTime(tournament.date)} • {tournament.location || "Location TBD"}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <Pill
                  tone={
                    tournament.status === "completed"
                      ? "green"
                      : tournament.status === "cancelled"
                      ? "red"
                      : tournament.status === "running"
                      ? "blue"
                      : "amber"
                  }
                >
                  Tournament: {tournament.status || "registering"}
                </Pill>
                <Pill tone={clock?.status === "running" ? "green" : "zinc"}>
                  Clock: {clock?.status || "stopped"}
                </Pill>
                <Pill tone="zinc">{activeEntrants.length} active</Pill>
                {bountyEnabled ? (
                  <Pill tone="amber">
                    ♠ Bounty {formatMoney(bountyAmount)} / life
                  </Pill>
                ) : null}
                {lateRegistrationEnabled ? (
                  <Pill tone={lateRegistrationOpen ? "green" : "zinc"}>
                    Late Reg: {lateRegistrationOpen ? "OPEN" : `L${lateRegistrationCutoffLevel || "—"}`}
                  </Pill>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate(`/tournament/${id}`)}
                className="rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-sm font-bold text-zinc-200"
              >
                Tournament Details
              </button>
              <button
                type="button"
                onClick={() =>
                  window.open(`/tournament/${id}/display`, "_blank", "noopener,noreferrer")
                }
                className="rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-sm font-bold text-zinc-200"
              >
                Display Mode
              </button>
              <button
                type="button"
                onClick={runPreflight}
                disabled={busyAction === "preflight"}
                className="rounded-xl px-4 py-3 text-sm font-black text-zinc-950 disabled:opacity-50"
                style={{ background: COPPER }}
              >
                {busyAction === "preflight" ? "Checking..." : "Run Preflight"}
              </button>
            </div>
          </div>
        </section>

        {error ? (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm font-semibold text-red-200">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-4 text-sm font-semibold text-green-200">
            {success}
          </div>
        ) : null}

        {preflight ? (
          <section
            className={`rounded-2xl border p-5 ${
              preflight.ready
                ? "border-green-500/30 bg-green-500/10"
                : "border-red-500/30 bg-red-500/10"
            }`}
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div
                  className={`text-lg font-black ${
                    preflight.ready ? "text-green-200" : "text-red-200"
                  }`}
                >
                  {preflight.ready
                    ? "✓ Tournament Manager Preflight Passed"
                    : "⚠ Tournament Manager Preflight Needs Attention"}
                </div>
                <div className="mt-1 text-sm text-zinc-300">
                  Server authorization and tournament data were verified.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(preflight.checks || []).map((check) => (
                  <Pill key={check.key} tone={check.ok ? "green" : "red"}>
                    {check.ok ? "✓" : "✕"} {check.label}
                  </Pill>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
          <Metric
            label="Active Players"
            value={activeEntrants.length}
            subtext={isCompleted ? "Tournament complete" : ""}
          />
          <Metric label="Eliminated" value={eliminatedEntrants.length} />
          <Metric
            label="Entries"
            value={Number(tournament.paidEntryCount || tournament.entrantsCount || entrants.length)}
          />
          <Metric
            label="Rebuys"
            value={entrants.reduce((sum, e) => sum + Math.max(0, Number(e.rebuys || 0)), 0)}
          />
          <Metric label="Regular Prize Pool" value={formatMoney(regularPrizePool)} />
          <Metric
            label={bountyEnabled ? "Bounty / Life" : "Buy-In"}
            value={formatMoney(bountyEnabled ? bountyAmount : tournament.buyIn)}
            subtext={bountyEnabled ? `${formatMoney(prizePoolContribution)} to prize pool / entry` : ""}
          />
        </section>

        {isCompleted ? (
          <section className="rounded-2xl border border-green-500/30 bg-green-500/5 p-5 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="text-xs font-black uppercase tracking-wider text-green-300">
                  Final Results
                </div>
                <h2 className="mt-1 text-2xl font-black text-zinc-100">
                  {tournament?.results?.completionType === "custom_chop"
                    ? "Tournament Completed — Custom Chop"
                    : "Tournament Completed"}
                </h2>
                <p className="mt-1 text-sm text-zinc-400">
                  Gameplay controls are locked. Results below are the official tournament record.
                </p>
              </div>
              <Pill tone="green">FINAL</Pill>
            </div>

            <div className="mt-5 overflow-hidden rounded-xl border border-zinc-800">
              <div className="grid grid-cols-[90px_minmax(0,1fr)_140px] bg-zinc-950 px-4 py-3 text-xs font-black uppercase tracking-wider text-zinc-500">
                <div>Finish</div>
                <div>Player</div>
                <div className="text-right">Payout</div>
              </div>

              {[...chopFinishedEntrants, ...eliminatedEntrants]
                .sort(
                  (a, b) =>
                    Number(a?.finishPlace || 9999) -
                    Number(b?.finishPlace || 9999)
                )
                .map((entrant) => (
                  <div
                    key={entrant.id}
                    className="grid grid-cols-[90px_minmax(0,1fr)_140px] items-center border-t border-zinc-800 px-4 py-4"
                  >
                    <div className="font-black text-[#B87333]">
                      {formatFinishPlace(entrant.finishPlace)}
                    </div>
                    <div>
                      <div className="font-black text-zinc-100">
                        {entrantName(entrant)}
                      </div>
                      <div className="mt-1 text-xs text-zinc-500">
                        {entrant.chopParticipant
                          ? "Custom Chop"
                          : entrant.eliminatedByName
                          ? `Eliminated by ${entrant.eliminatedByName}`
                          : "Final result"}
                      </div>
                    </div>
                    <div className="text-right font-black text-zinc-100">
                      {Number(entrant.negotiatedPayout || 0) > 0
                        ? formatMoney(entrant.negotiatedPayout)
                        : "—"}
                    </div>
                  </div>
                ))}
            </div>

            {tournament?.customChop ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <Metric
                  label="Chop Method"
                  value="Custom"
                  subtext={`${tournament.customChop.participants?.length || 0}-way agreement`}
                />
                <Metric
                  label="Prize Pool Allocated"
                  value={formatMoney(tournament.customChop.allocated || 0)}
                />
                <Metric
                  label="Left to Play"
                  value={formatMoney(tournament.customChop.leftToPlayFor || 0)}
                />
              </div>
            ) : null}
          </section>
        ) : null}

        {!isCompleted ? (
          <>
        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-[#B87333]">
                Live Clock
              </div>
              <h2 className="mt-1 text-2xl font-black text-zinc-100">
                {segmentLabel(activeSegment)}
              </h2>
              <div className="mt-3 text-6xl font-black tabular-nums text-white sm:text-7xl">
                {clock?.status === "running" || clock?.status === "paused"
                  ? msToMMSS(timeRemainingMs)
                  : "—"}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
              <button
                type="button"
                disabled={!canStart}
                onClick={startClock}
                className="rounded-xl bg-green-700 px-5 py-3 font-black text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-35"
              >
                Start
              </button>
              <button
                type="button"
                disabled={!canPause}
                onClick={pauseClock}
                className="rounded-xl bg-zinc-800 px-5 py-3 font-black text-zinc-100 hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-35"
              >
                Pause
              </button>
              <button
                type="button"
                disabled={!canResume}
                onClick={resumeClock}
                className="rounded-xl bg-[#B87333] px-5 py-3 font-black text-zinc-950 hover:bg-[#A46230] disabled:cursor-not-allowed disabled:opacity-35"
              >
                Resume
              </button>
              <button
                type="button"
                disabled={!!busyAction || !blinds.length}
                onClick={() => callControl("advance")}
                className="rounded-xl border border-zinc-700 bg-zinc-950 px-5 py-3 font-black text-zinc-200 disabled:opacity-35"
              >
                Next Level
              </button>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
            <div className="text-sm font-black text-amber-200">Manual / Recovery Control</div>
            <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(240px,1fr)_120px_120px_auto]">
              <select
                value={manualIndex}
                onChange={(e) => {
                  const nextIndex = Number(e.target.value);
                  setManualIndex(nextIndex);
                  setManualMinutes(String(Math.max(0, Number(blinds[nextIndex]?.minutes || 0))));
                  setManualSeconds("00");
                }}
                className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-zinc-100"
              >
                {blinds.map((item, index) => (
                  <option key={`${item.type || "segment"}-${index}`} value={index}>
                    {index + 1}. {segmentLabel(item)}
                  </option>
                ))}
              </select>

              <input
                type="number"
                min="0"
                value={manualMinutes}
                onChange={(e) => setManualMinutes(e.target.value)}
                className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-center font-bold text-zinc-100"
                aria-label="Minutes"
              />
              <input
                type="number"
                min="0"
                max="59"
                value={manualSeconds}
                onChange={(e) => setManualSeconds(e.target.value)}
                className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-center font-bold text-zinc-100"
                aria-label="Seconds"
              />

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setManualResume(false)}
                  className={`rounded-lg border px-3 py-2 text-xs font-black ${
                    !manualResume
                      ? "border-[#B87333] text-[#B87333]"
                      : "border-zinc-700 text-zinc-400"
                  }`}
                >
                  Apply Paused
                </button>
                <button
                  type="button"
                  onClick={() => setManualResume(true)}
                  className={`rounded-lg border px-3 py-2 text-xs font-black ${
                    manualResume
                      ? "border-[#B87333] text-[#B87333]"
                      : "border-zinc-700 text-zinc-400"
                  }`}
                >
                  Apply Running
                </button>
                <button
                  type="button"
                  disabled={!!busyAction}
                  onClick={applyManualClock}
                  className="rounded-lg bg-amber-700 px-4 py-2 text-xs font-black text-white disabled:opacity-40"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          {tournament?.addOnPause?.active ? (
            <div className="mt-4 flex flex-col gap-3 rounded-xl border border-blue-500/30 bg-blue-500/10 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-black text-blue-200">Add-On Pause Active</div>
                <div className="mt-1 text-sm text-blue-200/70">
                  Apply player add-ons, then resume into the next blind segment.
                </div>
              </div>
              <button
                type="button"
                disabled={!!busyAction}
                onClick={() => callControl("resume_after_addons")}
                className="rounded-xl bg-blue-700 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
              >
                Resume After Add-Ons
              </button>
            </div>
          ) : null}

          {tournament?.tableCombine?.active ? (
            <div className="mt-4 flex flex-col gap-3 rounded-xl border border-purple-500/30 bg-purple-500/10 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-black text-purple-200">Table Combine Pending</div>
                <div className="mt-1 text-sm text-purple-200/70">
                  Confirm players are in their new seats before resuming.
                </div>
              </div>
              <button
                type="button"
                disabled={!!busyAction}
                onClick={() => callControl("resume_after_combine")}
                className="rounded-xl bg-purple-700 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
              >
                Confirm & Resume
              </button>
            </div>
          ) : null}
        </section>

        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-[#B87333]">
                Late Registration
              </div>
              <h2 className="mt-1 text-xl font-black text-zinc-100">
                {lateRegistrationEnabled
                  ? lateRegistrationOpen
                    ? "OPEN"
                    : tournament.status === "registering"
                    ? "Configured — Opens When Tournament Starts"
                    : "CLOSED"
                  : "Disabled"}
              </h2>
              <p className="mt-1 text-sm text-zinc-500">
                {lateRegistrationEnabled
                  ? `Closes after Level ${lateRegistrationCutoffLevel || "—"}.`
                  : "Late registration is not enabled for this tournament."}
              </p>
            </div>

            {lateRegistrationOpen ? (
              <button
                type="button"
                disabled={!!busyAction}
                onClick={closeLateRegistration}
                className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm font-black text-red-200 disabled:opacity-40"
              >
                Close Late Registration Now
              </button>
            ) : null}
          </div>

          {lateRegistrationOpen ? (
            <div className="mt-5 grid gap-3 xl:grid-cols-[minmax(260px,1fr)_auto_auto] xl:items-center">
              <select
                value={lateMemberUid}
                onChange={(e) => setLateMemberUid(e.target.value)}
                className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-zinc-100"
              >
                <option value="">Select active member...</option>
                {availableLateMembers.map((member) => (
                  <option key={member.uid} value={member.uid}>
                    {member.name || member.displayName || member.email || member.uid}
                  </option>
                ))}
              </select>

              {tournament?.dealerAppreciationEnabled ? (
                <label className="flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-sm text-zinc-300">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#B87333]"
                    checked={lateDealerAppreciation}
                    onChange={(e) => setLateDealerAppreciation(e.target.checked)}
                  />
                  Dealer Appreciation
                </label>
              ) : null}

              <button
                type="button"
                disabled={!lateMemberUid || !!busyAction}
                onClick={addLateEntrant}
                className="rounded-xl bg-[#B87333] px-4 py-3 text-sm font-black text-zinc-950 disabled:opacity-40"
              >
                + Add Late Entrant
              </button>
            </div>
          ) : null}

          {lateRegistrationOpen ? (
            <div className="mt-4 rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 text-sm text-blue-200">
              Late entrants receive the normal starting stack, no Early Bird bonus, and are
              automatically placed into the best available table/seat.{" "}
              {bountyEnabled
                ? `They enter on Life 1 with a ${formatMoney(bountyAmount)} bounty.`
                : ""}
            </div>
          ) : null}
        </section>

        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-[#B87333]">
                Seating & Tables
              </div>
              <h2 className="mt-1 text-xl font-black text-zinc-100">
                Live Table Operations
              </h2>
              <p className="mt-1 text-sm text-zinc-500">
                Random assignment, balanced table combine, and manual player moves now run server-side.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!!busyAction || activeEntrants.length < 2}
                onClick={assignSeats}
                className="rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-sm font-black text-zinc-200 disabled:opacity-40"
              >
                Assign / Redraw Seats
              </button>
              <button
                type="button"
                disabled={!!busyAction || activeEntrants.length < 2}
                onClick={combineTables}
                className="rounded-xl bg-purple-700 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
              >
                Combine / Rebalance Tables
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(240px,1fr)_120px_120px_auto]">
            <select
              value={movePlayerId}
              onChange={(e) => setMovePlayerId(e.target.value)}
              className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-zinc-100"
            >
              <option value="">Move player...</option>
              {activeEntrants.map((entrant) => (
                <option key={entrant.id} value={entrant.id}>
                  {entrantName(entrant)} — T{entrant.tableNumber || "?"} S{entrant.seatNumber || "?"}
                </option>
              ))}
            </select>
            <input
              type="number"
              min="1"
              value={moveTable}
              onChange={(e) => setMoveTable(e.target.value)}
              className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-center font-bold text-zinc-100"
              placeholder="Table"
            />
            <input
              type="number"
              min="1"
              value={moveSeat}
              onChange={(e) => setMoveSeat(e.target.value)}
              className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-center font-bold text-zinc-100"
              placeholder="Seat"
            />
            <button
              type="button"
              disabled={!movePlayerId || !!busyAction}
              onClick={movePlayer}
              className="rounded-xl bg-[#B87333] px-4 py-3 text-sm font-black text-zinc-950 disabled:opacity-40"
            >
              Move Player
            </button>
          </div>
        </section>

        <div className="grid gap-6 2xl:grid-cols-[minmax(0,1.25fr)_minmax(380px,0.75fr)]">
          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
            <div className="flex items-end justify-between gap-3">
              <div>
                <h2 className="text-xl font-black text-zinc-100">Live Field</h2>
                <p className="mt-1 text-sm text-zinc-500">
                  Critical player actions below run through the admin Cloud Function.
                </p>
              </div>
              <Pill tone="green">{activeEntrants.length} active</Pill>
            </div>

            <div className="mt-5 space-y-3">
              {activeEntrants.map((entrant) => (
                <div
                  key={entrant.id}
                  className="flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-950 p-4 lg:flex-row lg:items-center lg:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <PositionChip role={positionRoleByEntrantId.get(entrant.id)} />
                    <div className="min-w-0">
                      <div className="font-black text-zinc-100">{entrantName(entrant)}</div>
                      <div className="mt-1 text-xs text-zinc-500">
                        Table {entrant.tableNumber || "—"} • Seat {entrant.seatNumber || "—"} •{" "}
                        {Number(entrant.rebuys || 0)} rebuy(s)
                      {bountyEnabled
                        ? ` • Life ${Number(entrant.currentLifeNumber || Number(entrant.rebuys || 0) + 1)} • ${formatMoney(
                            entrant.activeBountyAmount ?? bountyAmount
                          )} bounty • ${Number(entrant.knockouts || 0)} KO`
                        : ""}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {tournament.addOnsEnabled && !entrant.addOnTaken ? (
                      <button
                        type="button"
                        disabled={!!busyAction}
                        onClick={() => addOn(entrant)}
                        className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs font-black text-blue-200 disabled:opacity-40"
                      >
                        Add-On
                      </button>
                    ) : null}

                    <button
                      type="button"
                      disabled={!!busyAction || activeEntrants.length <= 1}
                      onClick={() => {
                        setElimTargetId(entrant.id);
                        setElimById("");
                      }}
                      className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-black text-red-200 disabled:opacity-40"
                    >
                      Eliminate
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {elimTargetId ? (
              <div className="mt-5 rounded-xl border border-red-500/30 bg-red-500/5 p-4">
                <div className="text-sm font-black text-red-200">
                  Record elimination:{" "}
                  {entrantName(entrants.find((e) => e.id === elimTargetId))}
                </div>
                <label className="mt-3 block text-xs font-black uppercase tracking-wider text-zinc-500">
                  Eliminated by
                </label>
                <select
                  value={elimById}
                  onChange={(e) => setElimById(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-zinc-100"
                >
                  <option value="">Select active player...</option>
                  {activeEntrants
                    .filter((entrant) => entrant.id !== elimTargetId)
                    .map((entrant) => (
                      <option key={entrant.id} value={entrant.id}>
                        {entrantName(entrant)}
                      </option>
                    ))}
                </select>

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setElimTargetId("");
                      setElimById("");
                    }}
                    className="rounded-lg border border-zinc-700 bg-zinc-950 px-4 py-2 text-sm font-bold text-zinc-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!elimById || !!busyAction}
                    onClick={eliminate}
                    className="rounded-lg bg-red-700 px-4 py-2 text-sm font-black text-white disabled:opacity-40"
                  >
                    Confirm Elimination
                  </button>
                </div>
              </div>
            ) : null}
          </section>

          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
            <h2 className="text-xl font-black text-zinc-100">Eliminated / Rebuy Queue</h2>
            <p className="mt-1 text-sm text-zinc-500">
              Eliminations remain visible here so a player can be rebought without losing the
              tournament record.
            </p>

            <div className="mt-5 space-y-3">
              {eliminatedEntrants.length ? (
                eliminatedEntrants.map((entrant) => (
                  <div
                    key={entrant.id}
                    className="rounded-xl border border-zinc-800 bg-zinc-950 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-black text-zinc-100">{entrantName(entrant)}</div>
                        <div className="mt-1 text-xs text-zinc-500">
                          Finish: {entrant.finishPlace || "—"} • Eliminated by{" "}
                          {entrant.eliminatedByName || "—"}
                        </div>
                      </div>
                      <Pill tone="red">OUT</Pill>
                    </div>

                    {tournament.rebuysEnabled ? (
                      <button
                        type="button"
                        disabled={!!busyAction}
                        onClick={() => rebuy(entrant)}
                        className="mt-3 w-full rounded-lg bg-[#B87333] px-4 py-2.5 text-sm font-black text-zinc-950 disabled:opacity-40"
                      >
                        Rebuy Player
                      </button>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-zinc-700 p-6 text-center text-sm text-zinc-500">
                  No eliminations yet.
                </div>
              )}
            </div>
          </section>
        </div>

        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-[#B87333]">
                Payout Control
              </div>
              <h2 className="mt-1 text-xl font-black text-zinc-100">
                Winner & Custom Chop
              </h2>
              <p className="mt-1 text-sm text-zinc-500">
                Regular prize pool: {formatMoney(regularPrizePool)}. Bounties stay separate.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {activeEntrants.length === 2 ? (
                <>
                  <select
                    value={winnerId}
                    onChange={(e) => setWinnerId(e.target.value)}
                    className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-sm text-zinc-100"
                  >
                    <option value="">Select winner...</option>
                    {activeEntrants.map((entrant) => (
                      <option key={entrant.id} value={entrant.id}>
                        {entrantName(entrant)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={!winnerId || !!busyAction}
                    onClick={declareWinner}
                    className="rounded-xl bg-green-700 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
                  >
                    Declare Winner
                  </button>
                </>
              ) : null}

              {activeEntrants.length >= 2 ? (
                <button
                  type="button"
                  disabled={!!busyAction}
                  onClick={openChop}
                  className="rounded-xl bg-[#B87333] px-4 py-3 text-sm font-black text-zinc-950 disabled:opacity-40"
                >
                  Custom Chop
                </button>
              ) : null}
            </div>
          </div>

          {showChop ? (
            <div className="mt-5 rounded-xl border border-[#B87333]/30 bg-[#B87333]/5 p-4">
              <div className="font-black text-zinc-100">Custom Negotiated Payouts</div>
              <div className="mt-1 text-sm text-zinc-500">
                Enter exactly what the remaining players agree to verbally. For a full chop, also assign the official finishing order.
              </div>

              <div className="mt-4 space-y-3">
                {activeEntrants.map((entrant) => (
                  <div
                    key={entrant.id}
                    className={`grid gap-2 ${
                      Number(leftToPlayFor || 0) <= 0
                        ? "sm:grid-cols-[minmax(0,1fr)_130px_180px]"
                        : "sm:grid-cols-[minmax(0,1fr)_180px]"
                    } sm:items-center`}
                  >
                    <div>
                      <div className="font-bold text-zinc-200">{entrantName(entrant)}</div>
                      {bountyEnabled ? (
                        <div className="text-xs text-zinc-500">
                          Own active bounty: {formatMoney(
                            entrant.activeBountyAmount ?? bountyAmount
                          )}
                        </div>
                      ) : null}
                    </div>
                    {Number(leftToPlayFor || 0) <= 0 ? (
                      <select
                        value={chopFinishPlaces[entrant.id] ?? ""}
                        onChange={(e) =>
                          setChopFinishPlaces((prev) => ({
                            ...prev,
                            [entrant.id]: e.target.value,
                          }))
                        }
                        className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 font-black text-zinc-100"
                        aria-label={`Finish place for ${entrantName(entrant)}`}
                      >
                        <option value="">Place</option>
                        {activeEntrants.map((_, index) => (
                          <option key={index + 1} value={index + 1}>
                            {index + 1}{index === 0 ? "st" : index === 1 ? "nd" : index === 2 ? "rd" : "th"}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={chopAmounts[entrant.id] ?? ""}
                      onChange={(e) =>
                        setChopAmounts((prev) => ({
                          ...prev,
                          [entrant.id]: e.target.value,
                        }))
                      }
                      className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-right font-black text-zinc-100"
                      placeholder="$0"
                    />
                  </div>
                ))}
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px] sm:items-center">
                <div>
                  <div className="font-bold text-zinc-200">Amount Left to Play For</div>
                  <div className="text-xs text-zinc-500">
                    Use $0 to end the tournament immediately.
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={leftToPlayFor}
                  onChange={(e) => setLeftToPlayFor(e.target.value)}
                  className="rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 text-right font-black text-zinc-100"
                />
              </div>

              <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-sm">
                <div className="flex justify-between gap-3 text-zinc-400">
                  <span>Regular Prize Pool</span>
                  <strong className="text-zinc-100">{formatMoney(regularPrizePool)}</strong>
                </div>
                <div className="mt-1 flex justify-between gap-3 text-zinc-400">
                  <span>Allocated + Left to Play</span>
                  <strong
                    className={
                      Math.abs(
                        activeEntrants.reduce(
                          (sum, entrant) => sum + Number(chopAmounts[entrant.id] || 0),
                          0
                        ) +
                          Number(leftToPlayFor || 0) -
                          regularPrizePool
                      ) < 0.01
                        ? "text-green-300"
                        : "text-red-300"
                    }
                  >
                    {formatMoney(
                      activeEntrants.reduce(
                        (sum, entrant) => sum + Number(chopAmounts[entrant.id] || 0),
                        0
                      ) + Number(leftToPlayFor || 0)
                    )}
                  </strong>
                </div>
              </div>

              {bountyEnabled ? (
                <div className="mt-3 rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-xs text-blue-200">
                  Full chop ($0 left to play): each surviving player receives their own
                  {` ${formatMoney(bountyAmount)} `}active bounty back. If money remains to play
                  for, active bounties stay live.
                </div>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setShowChop(false)}
                  className="rounded-lg border border-zinc-700 bg-zinc-950 px-4 py-2 text-sm font-bold text-zinc-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!!busyAction}
                  onClick={submitCustomChop}
                  className="rounded-lg bg-[#B87333] px-4 py-2 text-sm font-black text-zinc-950 disabled:opacity-40"
                >
                  Confirm Custom Chop
                </button>
              </div>
            </div>
          ) : null}
        </section>

          </>
        ) : null}

        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
          <div>
            <h2 className="text-xl font-black text-zinc-100">Tournament Timeline</h2>
            <p className="mt-1 text-sm text-zinc-500">Latest 30 recorded tournament events.</p>
          </div>

          <div className="mt-5 space-y-3">
            {events.length ? (
              events.map((event) => (
                <div key={event.id} className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="font-bold text-zinc-100">
                        {event.title || "Tournament activity"}
                      </div>
                      {event.message ? (
                        <div className="mt-1 text-sm text-zinc-400">{event.message}</div>
                      ) : null}
                    </div>
                    <div className="text-xs text-zinc-600">{formatDateTime(event.createdAt)}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-zinc-500">No timeline events yet.</div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
